import type { FeatureType, PermissionAction, PermissionModule } from 'src/auth/types/permissions';
import type { NavItemBaseProps, NavProps } from 'src/components/nav-section/types';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

export type SearchPageAccess = {
  feature?: FeatureType;
  modules: readonly PermissionModule[];
  action: PermissionAction;
};

export type SearchItem = {
  title: string;
  path: string;
  section: string;
  parents: string[];
  keywords: string[];
  kind: 'page' | 'action';
};

type ActionPageDefinition = {
  title: string;
  path: string;
  parentPath: string;
  parentTitle: string;
  section: string;
  access: SearchPageAccess;
  keywords?: readonly string[];
};

type RankedSearchItem = {
  item: SearchItem;
  score: number;
  index: number;
};

// Synonyms live in one typed catalog so product language can evolve without
// coupling semantic search rules to the modal component.
const PAGE_KEYWORDS: Record<string, readonly string[]> = {
  [paths.dashboard.root]: ['painel', 'inicio', 'visao geral', 'overview', 'resumo'],
  [paths.dashboard.rules.root]: ['regra', 'regras', 'pontuacao', 'motor', 'rule'],
  [paths.dashboard.conversionRate.root]: ['taxa', 'conversao', 'rate', 'multiplicador'],
  [paths.dashboard.variables]: ['variavel', 'variaveis', 'variable'],
  [paths.dashboard.invoices.root]: ['nota fiscal', 'notas fiscais', 'nf', 'invoice', 'scan'],
  [paths.dashboard.giveaways.root]: ['sorteio', 'sorteios', 'giveaway', 'raffle'],
  [paths.dashboard.campaigns.root]: ['campanha', 'campanhas', 'marketing', 'promocao'],
  [paths.dashboard.adminUsers.root]: ['admin', 'administrador', 'equipe', 'staff', 'acesso'],
  [paths.dashboard.adminLogs]: ['auditoria', 'historico', 'atividade', 'log', 'logs'],
  [paths.dashboard.appUsers.root]: ['cliente', 'clientes', 'consumidor', 'customer', 'membro'],
  [paths.dashboard.webAppUsers.root]: ['usuario web', 'portal', 'web user'],
  [paths.dashboard.establishments.root]: ['loja', 'lojas', 'parceiro', 'merchant', 'store', 'restaurante'],
  [paths.dashboard.establishments.categories]: ['categoria de loja', 'segmento de estabelecimento'],
  [paths.dashboard.events.root]: ['evento', 'eventos', 'beneficio', 'experiencia'],
  [paths.dashboard.coupons.root]: ['cupom', 'cupons', 'voucher', 'desconto'],
  [paths.dashboard.products.root]: ['produto', 'produtos', 'item', 'recompensa'],
  [paths.dashboard.redeems.root]: ['resgate', 'resgates', 'troca', 'redeem'],
  [paths.dashboard.terms.root]: ['termo', 'termos', 'contrato', 'terms'],
  [paths.dashboard.privacy.root]: ['privacidade', 'politica', 'lgpd', 'privacy'],
  [paths.dashboard.faq.root]: ['duvida', 'duvidas', 'pergunta frequente', 'ajuda', 'help'],
  [paths.dashboard.faq.categories]: ['categoria de faq', 'categoria de duvida'],
  [paths.dashboard.shoppingSettings.root]: ['dados do cliente', 'configuracao do shopping', 'tenant', 'whitelabel'],
  [paths.dashboard.userLevels.root]: ['tier', 'tiers', 'level', 'levels', 'nivel', 'niveis', 'hierarquia', 'faixa de usuario'],
  [paths.dashboard.keepUp.root]: ['noticia', 'noticias', 'conteudo', 'feed', 'blog'],
  [paths.dashboard.highlights.root]: ['destaque', 'destaques', 'featured', 'vitrine'],
  [paths.dashboard.stories.root]: ['story', 'stories', 'conteudo temporario'],
  [paths.dashboard.banners.root]: ['banner', 'banners', 'publicidade', 'midia'],
  [paths.dashboard.home.root]: ['inicio do app', 'home do app', 'pagina inicial', 'vitrine'],
  [paths.dashboard.notifications.root]: ['push', 'mensagem', 'notificacao', 'notificacoes', 'disparo'],
  [paths.dashboard.notifications.segments]: ['audiencia', 'publico', 'segmentacao', 'segmento'],
  [paths.dashboard.notifications.logs]: ['historico de notificacao', 'entrega', 'envio'],
};

const CREATE_KEYWORDS = ['criar', 'novo', 'nova', 'adicionar', 'cadastrar', 'cadastro', 'incluir', 'create', 'new'];

const ACTION_PAGES: readonly ActionPageDefinition[] = [
  {
    title: 'Criar regra',
    path: paths.dashboard.rules.create,
    parentPath: paths.dashboard.rules.root,
    parentTitle: 'Gerenciar Regras',
    section: 'Motor de pontos',
    access: { feature: 'motor', modules: ['motorRules'], action: 'create' },
  },
  {
    title: 'Criar taxa de conversão',
    path: paths.dashboard.conversionRate.create,
    parentPath: paths.dashboard.conversionRate.root,
    parentTitle: 'Taxas de Conversão',
    section: 'Motor de pontos',
    access: { feature: 'motor', modules: ['motorConversionRates'], action: 'create' },
  },
  {
    title: 'Criar administrador',
    path: paths.dashboard.adminUsers.create,
    parentPath: paths.dashboard.adminUsers.root,
    parentTitle: 'Administradores',
    section: 'Configurações gerais',
    access: { modules: ['adminUsers'], action: 'create' },
  },
  {
    title: 'Criar usuário do Web App',
    path: paths.dashboard.webAppUsers.create,
    parentPath: paths.dashboard.webAppUsers.root,
    parentTitle: 'Usuários do Web App',
    section: 'Configurações gerais',
    access: { modules: ['webappUsers'], action: 'create' },
  },
  {
    title: 'Criar sorteio',
    path: paths.dashboard.giveaways.create,
    parentPath: paths.dashboard.giveaways.root,
    parentTitle: 'Gerenciar Sorteios',
    section: 'Motor de pontos',
    access: { feature: 'motor', modules: ['motorGiveaways'], action: 'create' },
  },
  {
    title: 'Criar campanha',
    path: paths.dashboard.campaigns.create,
    parentPath: paths.dashboard.campaigns.root,
    parentTitle: 'Campanhas',
    section: 'Motor de pontos',
    access: { feature: 'motor', modules: ['motorCampaigns'], action: 'create' },
  },
  {
    title: 'Criar evento',
    path: paths.dashboard.events.create,
    parentPath: paths.dashboard.events.root,
    parentTitle: 'Eventos',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appEvents'], action: 'create' },
  },
  {
    title: 'Criar cupom',
    path: paths.dashboard.coupons.create,
    parentPath: paths.dashboard.coupons.root,
    parentTitle: 'Cupons',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appCoupons'], action: 'create' },
  },
  {
    title: 'Criar produto',
    path: paths.dashboard.products.create,
    parentPath: paths.dashboard.products.root,
    parentTitle: 'Produtos',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appProducts'], action: 'create' },
  },
  {
    title: 'Criar termo de uso',
    path: paths.dashboard.terms.create,
    parentPath: paths.dashboard.terms.root,
    parentTitle: 'Termos de Uso',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appTermsOfUse'], action: 'create' },
  },
  {
    title: 'Criar política de privacidade',
    path: paths.dashboard.privacy.create,
    parentPath: paths.dashboard.privacy.root,
    parentTitle: 'Política de Privacidade',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appPrivacyAndPolicy'], action: 'create' },
  },
  {
    title: 'Criar FAQ',
    path: paths.dashboard.faq.create,
    parentPath: paths.dashboard.faq.root,
    parentTitle: 'Gerenciar FAQs',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appFaqs'], action: 'create' },
  },
  {
    title: 'Criar categoria de FAQ',
    path: paths.dashboard.faq.createCategory,
    parentPath: paths.dashboard.faq.categories,
    parentTitle: 'Gerenciar Categorias de FAQ',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appFaqCategories'], action: 'create' },
  },
  {
    title: 'Criar estabelecimento',
    path: paths.dashboard.establishments.create,
    parentPath: paths.dashboard.establishments.root,
    parentTitle: 'Estabelecimentos',
    section: 'Aplicativo',
    access: {
      feature: 'app',
      modules: ['appStoresEstablishments', 'appFoodEstablishments', 'appServiceEstablishments'],
      action: 'create',
    },
  },
  {
    title: 'Criar categoria de estabelecimento',
    path: paths.dashboard.establishments.createCategory,
    parentPath: paths.dashboard.establishments.categories,
    parentTitle: 'Categorias de Estabelecimentos',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appEstablishmentCategories'], action: 'create' },
  },
  {
    title: 'Criar conteúdo do Fique por Dentro',
    path: paths.dashboard.keepUp.create,
    parentPath: paths.dashboard.keepUp.root,
    parentTitle: 'Fique por Dentro',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appKeepUps'], action: 'create' },
  },
  {
    title: 'Criar destaque',
    path: paths.dashboard.highlights.create,
    parentPath: paths.dashboard.highlights.root,
    parentTitle: 'Destaques',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appHighlights'], action: 'create' },
  },
  {
    title: 'Criar story',
    path: paths.dashboard.stories.create,
    parentPath: paths.dashboard.stories.root,
    parentTitle: 'Stories',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appStories'], action: 'create' },
  },
  {
    title: 'Criar seção da Home',
    path: paths.dashboard.home.create,
    parentPath: paths.dashboard.home.root,
    parentTitle: 'Home',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appHome'], action: 'create' },
  },
  {
    title: 'Criar notificação',
    path: paths.dashboard.notifications.createCampaign,
    parentPath: paths.dashboard.notifications.root,
    parentTitle: 'Gerenciar Notificações',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appNotifications'], action: 'create' },
  },
  {
    title: 'Criar segmento de audiência',
    path: paths.dashboard.notifications.createSegment,
    parentPath: paths.dashboard.notifications.segments,
    parentTitle: 'Segmentos de Audiência',
    section: 'Aplicativo',
    access: { feature: 'app', modules: ['appNotifications'], action: 'create' },
  },
];

// ----------------------------------------------------------------------

export const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim()
    .replace(/\s+/g, ' ');

const normalizePath = (path: string) => (path === '/' ? path : path.replace(/\/+$/, ''));

const flattenNavItem = (item: NavItemBaseProps, section: string, parents: string[], result: SearchItem[]) => {
  if (item.disabled) {
    return;
  }

  const path = normalizePath(item.path);

  if (path.startsWith('/')) {
    result.push({
      title: item.title,
      path,
      section,
      parents,
      keywords: [...(PAGE_KEYWORDS[path] ?? [])],
      kind: 'page',
    });
  }

  if (Array.isArray(item.children)) {
    item.children.forEach((child: NavItemBaseProps) => {
      flattenNavItem(child, section, [...parents, item.title], result);
    });
  }
};

export function buildSearchItems(data: NavProps['data'], canAccess: (access: SearchPageAccess) => boolean): SearchItem[] {
  const items: SearchItem[] = [
    {
      title: 'Dashboard',
      path: paths.dashboard.root,
      section: 'Visão geral',
      parents: [],
      keywords: [...(PAGE_KEYWORDS[paths.dashboard.root] ?? [])],
      kind: 'page',
    },
  ];

  data.forEach((section) => {
    section.items.forEach((item) => flattenNavItem(item, section.subheader, [], items));
  });

  ACTION_PAGES.filter((page) => canAccess(page.access)).forEach((page) => {
    items.push({
      title: page.title,
      path: normalizePath(page.path),
      section: page.section,
      parents: [page.parentTitle],
      keywords: [...CREATE_KEYWORDS, ...(PAGE_KEYWORDS[page.parentPath] ?? []), ...(page.keywords ?? [])],
      kind: 'action',
    });
  });

  return Array.from(new Map(items.map((item) => [item.path, item])).values());
}

type FieldWeights = {
  exact: number;
  prefix: number;
  contains: number;
};

const scoreField = (value: string, term: string, weights: FieldWeights) => {
  const normalizedValue = normalizeSearchText(value);
  const words = normalizedValue.split(/[\s/_.-]+/).filter(Boolean);

  if (normalizedValue === term || words.includes(term)) {
    return weights.exact;
  }

  if (normalizedValue.startsWith(term) || words.some((word) => word.startsWith(term))) {
    return weights.prefix;
  }

  if (normalizedValue.includes(term)) {
    return weights.contains;
  }

  return 0;
};

const scoreTerm = (item: SearchItem, term: string) =>
  Math.max(
    scoreField(item.title, term, { exact: 120, prefix: 105, contains: 90 }),
    ...item.keywords.map((keyword) => scoreField(keyword, term, { exact: 80, prefix: 70, contains: 60 })),
    scoreField(item.path, term, { exact: 55, prefix: 50, contains: 45 }),
    ...item.parents.map((parent) => scoreField(parent, term, { exact: 45, prefix: 40, contains: 35 })),
    scoreField(item.section, term, { exact: 30, prefix: 25, contains: 20 })
  );

export function searchItems(items: SearchItem[], query: string): SearchItem[] {
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) {
    return items;
  }

  const terms = normalizedQuery.split(' ');

  return items
    .map<RankedSearchItem>((item, index) => {
      const termScores = terms.map((term) => scoreTerm(item, term));

      if (termScores.some((score) => score === 0)) {
        return { item, score: 0, index };
      }

      const normalizedTitle = normalizeSearchText(item.title);
      let phraseBonus = 0;

      if (normalizedTitle === normalizedQuery) {
        phraseBonus = 80;
      } else if (normalizedTitle.startsWith(normalizedQuery)) {
        phraseBonus = 50;
      }

      const actionBonus = item.kind === 'action' && CREATE_KEYWORDS.includes(normalizedQuery) ? 25 : 0;

      return {
        item,
        score: termScores.reduce((total, score) => total + score, 0) + phraseBonus + actionBonus,
        index,
      };
    })
    .filter((result) => result.score > 0)
    .sort((resultA, resultB) => resultB.score - resultA.score || resultA.index - resultB.index)
    .map((result) => result.item);
}
